import React from 'react';

const SECBurstSessionsList = ({
  sessions,
  loading,
  onDelete,
  deletingSessionId
}) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'scheduled':
        return <span className="badge bg-primary">Scheduled</span>;
      case 'active':
        return <span className="badge bg-success">Active</span>;
      case 'completed':
        return <span className="badge bg-secondary">Completed</span>;
      case 'cancelled':
        return <span className="badge bg-danger">Cancelled</span>;
      default:
        return <span className="badge bg-light text-dark">{status}</span>;
    }
  };

  const getEndTime = (startTime, durationMinutes) => {
    const start = new Date(startTime);
    return new Date(start.getTime() + durationMinutes * 60000);
  };

  const getProgress = (session) => {
    if (session.status === 'completed' || session.status === 'cancelled') {
      return 100;
    }
    if (session.status === 'scheduled') {
      return 0;
    }

    const now = new Date();
    const start = new Date(session.start_time);
    const end = getEndTime(session.start_time, session.duration_minutes);

    if (now < start) return 0;
    if (now > end) return 100;

    const total = end.getTime() - start.getTime();
    const elapsed = now.getTime() - start.getTime();
    return Math.round((elapsed / total) * 100);
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading burst sessions...</p>
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="alert alert-info">
        <i className="bi bi-info-circle me-2"></i>
        No burst polling sessions scheduled. Use the lightning bolt button on a schedule to create one.
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <table className="table table-hover">
        <thead>
          <tr>
            <th>Name</th>
            <th>Status</th>
            <th>Start Time</th>
            <th>Duration</th>
            <th>Progress</th>
            <th>Polls</th>
            <th>Filings Found</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sessions.map(session => {
            const progress = getProgress(session);
            const endTime = getEndTime(session.start_time, session.duration_minutes);
            
            return (
              <tr key={session.session_id}>
                <td>
                  <strong>{session.name}</strong>
                </td>
                <td>{getStatusBadge(session.status)}</td>
                <td>
                  <div>{formatDate(session.start_time)}</div>
                  <small className="text-muted">to {formatDate(endTime)}</small>
                </td>
                <td>{session.duration_minutes} min</td>
                <td style={{ minWidth: '120px' }}>
                  <div className="progress" style={{ height: '20px' }}>
                    <div
                      className={`progress-bar ${
                        session.status === 'active' ? 'progress-bar-striped progress-bar-animated bg-success' :
                        session.status === 'completed' ? 'bg-secondary' :
                        session.status === 'cancelled' ? 'bg-danger' : 'bg-primary'
                      }`}
                      role="progressbar"
                      style={{ width: `${progress}%` }}
                      aria-valuenow={progress}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    >
                      {progress}%
                    </div>
                  </div>
                </td>
                <td>
                  <span className="badge bg-light text-dark">
                    {session.polls_executed} / ~{session.duration_minutes}
                  </span>
                </td>
                <td>
                  {session.filings_found > 0 ? (
                    <span className="badge bg-success">{session.filings_found}</span>
                  ) : (
                    <span className="badge bg-secondary">0</span>
                  )}
                </td>
                <td>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => onDelete(session.session_id)}
                    disabled={deletingSessionId === session.session_id}
                    title="Delete session"
                  >
                    {deletingSessionId === session.session_id ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    ) : (
                      <i className="bi bi-trash"></i>
                    )}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default SECBurstSessionsList;
